import { OtpEnum } from 'api-hooks/common/model';
import { useSendOtp, useVerifyOtp } from 'api-hooks/common/mutation';
import Button from 'components/elements/button';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import PinInput from 'react-pin-input';
import { useToasts } from 'react-toast-notifications';
import color from 'styles/color';
import typography from 'styles/typography';

import Separator from '../separator';
import Stack from '../stack';
import Text from '../text';

interface Props {
  email: string;
  title?: string;
  onSubmit: (verificationToken: string) => void;
  setStep?: () => void;
  type: OtpEnum;
}

export default function Verification(props: Props) {
  const { t } = useTranslation();
  const {
    email,
    onSubmit,
    title = t('common:enter_the_otp_code'),
    setStep,
    type,
  } = props;
  const firstTime = React.useRef(false);
  const [otp, setOtp] = React.useState('');
  const [timer, setTimer] = React.useState(0);
  const { mutateAsync: mutateSendOtp } = useSendOtp();
  const { mutateAsync: mutateVerifyOtp } = useVerifyOtp();
  const toast = useToasts();

  const onSendOTP = React.useCallback(async () => {
    try {
      const response = await mutateSendOtp({ email, type });
      toast.addToast(response.message, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      toast.addToast(error.message, { appearance: 'error', autoDismiss: true });
    }
  }, [email, mutateSendOtp, toast, type]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (firstTime.current) return;
    onSendOTP();
    setTimer(60);
    firstTime.current = true;
  }, [onSendOTP]);

  const onResendCode = React.useCallback(() => {
    if (timer > 0) return;
    onSendOTP();
    setTimer(60);
  }, [onSendOTP, timer]);

  const onVerifyOtp = React.useCallback(async () => {
    try {
      const result = await mutateVerifyOtp({
        type,
        email,
        otp,
      });

      onSubmit(result.verificationToken);
    } catch (error) {
      toast.addToast(error.message, { appearance: 'error', autoDismiss: true });
    }
  }, [email, mutateVerifyOtp, onSubmit, otp, toast, type]);

  React.useEffect(() => {
    if (timer) {
      setTimeout(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
  }, [timer]);

  return (
    <>
      <Text
        weight="bold"
        css={{
          ...typography.headline4,
          '@md': {
            ...typography.headline3,
          },
        }}
        align="center"
      >
        {title}
      </Text>
      <Separator gap={8} />
      <Text align="center" variant="body3">
        {t(`common:enter_the_otp_code_message_${type}`, {
          extra: email,
        })}
      </Text>
      <Separator gap={16} />
      <Stack
        rootCss={{
          '& .pincode-input-text': {
            ...typography.headline7,
            width: '24px !important',
            height: '24px !important',
            '@media(min-width: 375px)': {
              width: '32px !important',
              height: '32px !important',
            },
            '@md': {
              ...typography.headline3,
              width: '50px !important',
              height: '50px !important',
            },
          },
        }}
      >
        <PinInput
          length={6}
          initialValue=""
          type="numeric"
          inputMode="number"
          inputStyle={{
            border: 'none',
            borderBottom: `2px solid ${color.neutral8}`,
            marginLeft: 8,
            marginRight: 8,
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 16,
          }}
          onChange={setOtp}
          onComplete={setOtp}
        />
      </Stack>
      <Separator gap={16} />
      <Button
        variant="tertiary"
        onClick={onResendCode}
        css={{ width: '100%' }}
        disabled={!!timer}
      >
        {t('common:resend', {
          extra: timer
            ? t('common:extra_seconds', { extra: timer.toString() })
            : '',
        })}
      </Button>
      <Separator gap={16} />
      <Button
        onClick={onVerifyOtp}
        css={{ width: '100%' }}
        disabled={otp.length !== 6}
      >
        {t('common:verification')}
      </Button>
      <Separator gap={8} />
      {setStep && (
        <Button
          variant="tertiary"
          onClick={() => setStep && setStep()}
          css={{ width: '100%' }}
        >
          {t('common:back')}
        </Button>
      )}
    </>
  );
}
